import React from 'react';
import styled from '@benzinga/themetron';
import { CoinInput, CryptoDotComCoinResponse } from './entities';

export const formatPercentage = (priceChange: number): string => {
  const percent = (priceChange * 100).toFixed(2);
  return priceChange > 0 ? `+${percent}%` : `${percent}%`;
};

export const getChartColorFromPrices = (prices: number[]): React.CSSProperties['color'] => {
  const price1 = prices[0];
  const price2 = prices[prices.length - 1];
  if (price1 > price2) return '#e64b60';
  else if (price1 < price2) return '#20bca4';
  else return '#000000';
};

export const formatPricesToChartPoints = (prices: number[]) => {
  let min = prices[0];
  const points = prices
    .map(price => {
      if (price < min) min = price;
      return price;
    })
    .map(price => price / min - 1);
  return points;
};

export const formatPrice = (price: number) => {
  const numberOfZeros = (value: number) => {
    let c = 0;
    while (!~~value) {
      c++;
      value *= 10;
    }
    return c - 1;
  };

  const numberBeforeDecimal = Number(price.toString().split('.')[0]);

  if (numberBeforeDecimal > 0) {
    return price.toFixed(2);
  } else if (numberBeforeDecimal <= 0) {
    return price.toFixed(numberOfZeros(price) + 4);
  }

  return price;
};

export const getCoinIcon = (coinSlug: string) => `https://static.crypto.com/token/icons/${coinSlug}/color_icon.png`;

export const CryptoDotComLogo: React.FC = () => (
  <svg focusable="false" viewBox="0 0 10 12">
    <path
      clipRule="evenodd"
      d="M6.57496 9.09872H5.90614L5.1051 8.3611V7.98604L5.93582 7.18906V5.93135L7.02287 5.22407L8.26304 6.15732L6.57496 9.09872ZM3.80051 7.11216L3.92429 5.92696L3.51922 4.86993H5.923L5.52704 5.92696L5.64171 7.10777L4.71689 7.11216H3.80051ZM4.34623 8.3611L3.54485 9.10716H2.86726L1.17109 6.15732L2.41531 5.23251L3.51517 5.93135V7.18906L4.34623 7.98604V8.3611ZM2.85478 2.69179H6.56687L7.01443 4.58426H2.42374L2.85478 2.69179ZM4.7169 0.336914L6.10352e-05 3.0601V8.5068L4.7169 11.2303L9.43407 8.5068V3.0601L4.7169 0.336914Z"
      fill="#1E3F6F"
      fillRule="evenodd"
    ></path>
  </svg>
);

export const getCoinsData = async (
  coins: CryptoDotComCoinResponse[] | CoinInput[],
): Promise<CryptoDotComCoinResponse[]> => {
  let coinsData: CryptoDotComCoinResponse[] = [];
  const fetches: Promise<CryptoDotComCoinResponse>[] = [];

  const fetchCoin = (coin: string) => {
    return fetch(`https://price-api.crypto.com/price/v1/tokens/${coin}`).then(res => res.json());
  };

  coins.forEach((coin: CoinInput) => fetches.push(fetchCoin(coin.slug)));

  const allData = Promise.allSettled(fetches);
  await allData.then(result => {
    const fulfilledResults = result?.filter(
      result => result.status === 'fulfilled' && !!result.value,
    ) as PromiseFulfilledResult<CryptoDotComCoinResponse>[];
    coinsData = fulfilledResults.map(result => result.value);
  });

  return coinsData;
};

export const CryptoDotComLink: React.FC = () => (
  <CryptoDotComLinkStyled
    className="crypto-dot-com-link"
    href="https://crypto.com/"
    rel="noopener noreferrer"
    target="_blank"
  >
    <span className="powered-by">Powered by</span>
    <CryptoDotComLogo />
    <span className="crypto-com">crypto.com</span>
  </CryptoDotComLinkStyled>
);

const CryptoDotComLinkStyled = styled.a`
  &.crypto-dot-com-link {
    width: 170px;
    display: flex;
    align-items: center;
    color: #1199fa;
    font-size: 13px;
    font-weight: 400;
    margin-top: 13px;

    svg {
      width: 16px;
      height: 16px;
      margin: 0 4px;
    }
    @media (max-width: 700px) {
      margin-left: 0.5rem;
    }
  }
`;
